import React from 'react';
import ReactTooltip from 'react-tooltip';
import { defaultTemplate } from 'core/hoc';
import {
	Loader,
	BaasicHeader,
	BaasicDropdown,
	BaasicInput,
	BaasicCheckbox,
	BaasicButton,
	BaasicDropzone,
} from 'core/components';
import { formatDate, translate } from 'core/utils';
import { observer } from 'mobx-react';
import _ from 'lodash';

const RateCalculatorTemplate = ({ viewStore, t }) => {
	const { loading, form, carrierTypeRateGroups, openModal } = viewStore;
	if (loading) return <Loader />;
	return (
		<form onSubmit={form.onSubmit}>
			<ReactTooltip />
			<BaasicHeader title="MAINMENU.RATECALCULATOR.TITLE">
				<div className="flex--primary flex--align--middle align--h--right">
					<BaasicButton
						className="btn btn--med btn--primary spc--right--med"
						type="button"
						onClick={() =>
							openModal({
								data: { ...viewStore },
								component: ImportParametersXlsModal,
							})
						}
						label="RATECALCULATOR.IMPORTXLS"
					/>
				</div>
			</BaasicHeader>
			<div className="rate-calc__wrap">
				<RateCalculatorParametersInput {...viewStore} t={t} />
				{carrierTypeRateGroups.length > 0 && <CarrierRates {...viewStore} t={t} />}
			</div>
		</form>
	);
};

const RateCalculatorParametersInput = observer(props => {
	const { carrierDropdownStore, form } = props;
	return (
		<div className="rate-calc__item is-first">
			<ShipFrom {...props} />
			<ShipTo {...props} />
			<PackageDimensions {...props} />
			<CarriersToLoad {...props} />
			<div className="spc--bottom--sml">
				<BaasicCheckbox field={form.$('showCheapestRate')} />
			</div>
			<div className="spc--bottom--sml">
				<BaasicCheckbox
					field={form.$('useThirdPartyAccounts')}
					onChangeCallback={async e => {
						await carrierDropdownStore.updateFilter({
							excludeThirdPartyCarriers: !e.currentTarget.checked,
							page: 1,
							rpp: 25,
						});
					}}
				/>
			</div>
			<BaasicButton
				className="btn btn--med btn--primary spc--bottom--sml"
				type="submit"
				label="RATECALCULATOR.GETRATES"
			/>
		</div>
	);
});

const ShipFrom = observer(props => {
	const { warehouseDropdownStore, form, t } = props;
	return (
		<div className="card card--med spc--bottom--sml">
			<div className="split__box__header">
				<div className="split__box__header__title">{t('RATECALCULATOR.HEADER.SHIPFROM')}</div>
			</div>
			<div className="spc--bottom--sml">
				<BaasicDropdown
					dropdownStore={warehouseDropdownStore}
					additionalLabel={() => (
						<i
							data-tip={t('RATECALCULATOR.TOOLTIP.WAREHOUSEORZIPCODE')}
							className="fas fa-exclamation-circle spc--left--tny"
						></i>
					)}
				/>
			</div>
			<BaasicInput field={form.$('shipFromZipCode')} showCheckmark={false} />
		</div>
	);
});

const ShipTo = observer(props => {
	const { form, t } = props;
	return (
		<div className="card card--med spc--bottom--sml">
			<div className="split__box__header">
				<div className="split__box__header__title">{t('RATECALCULATOR.HEADER.SHIPTO')}</div>
			</div>
			<BaasicInput field={form.$('shipToZipCode')} showCheckmark={false} />
			<div>
				<BaasicCheckbox field={form.$('isResidentialRecipient')} />
			</div>
		</div>
	);
});

const PackageDimensions = observer(props => {
	const { productDropdownStore, t, form } = props;
	return (
		<div className="card card--med spc--bottom--sml">
			<div className="split__box__header">
				<div className="split__box__header__title">{t('RATECALCULATOR.HEADER.PRODUCTDETAILS')}</div>
			</div>
			<div className="spc--bottom--sml">
				<BaasicDropdown
					dropdownStore={productDropdownStore}
					additionalLabel={() => (
						<i
							data-tip={t('RATECALCULATOR.TOOLTIP.PRODUCTDIMENSION')}
							className="fas fa-exclamation-circle spc--left--tny"
						></i>
					)}
				/>
			</div>
			<BaasicInput field={form.$('weight')} showCheckmark={false} />
			<div className="row">
				<div className="col col-sml-4">
					<BaasicInput field={form.$('length')} showCheckmark={false} />
				</div>
				<div className="col col-sml-4">
					<BaasicInput field={form.$('width')} showCheckmark={false} />
				</div>
				<div className="col col-sml-4">
					<BaasicInput field={form.$('height')} showCheckmark={false} />
				</div>
			</div>
		</div>
	);
});

const CarriersToLoad = observer(props => {
	const { carrierDropdownStore, t } = props;
	return (
		<div className="card card--med spc--bottom--sml">
			<div className="split__box__header">
				<div className="split__box__header__title">{t('RATECALCULATOR.HEADER.CARRIERSTOLOAD')}</div>
			</div>
			<div className="spc--bottom--sml">
				<BaasicDropdown
					dropdownStore={carrierDropdownStore}
					additionalLabel={() => (
						<i
							data-tip={t('RATECALCULATOR.TOOLTIP.CARRIERSTOLOAD')}
							className="fas fa-exclamation-circle spc--left--tny"
						></i>
					)}
				/>
			</div>
		</div>
	);
});

const CarrierRates = observer(props => {
	const { carrierTypeRateGroups, t } = props;
	return (
		<React.Fragment>
			{_.map(carrierTypeRateGroups, (carrierTypeRateGroup, idx) => {
				return (
					<div className="rate-calc__item" key={idx}>
						<div className="card card--med card--rate-calc">
							<div className={`rate-calc__logo ${carrierTypeRateGroup.className} spc--bottom--sml`}></div>
							<div className="flex--default  flex--align--h--center spc--bottom--med">
								<div className="flex--default flex--gap--1">
									<div className="type--center type--wgt--bold type--med">{carrierTypeRateGroup.carrierType}</div>
									{carrierTypeRateGroup.rateZone != '' && (
										<div className="rate-calc__zone">
											<span>{t('RATECALCULATOR.RATEZONE')}</span>
											<span>{carrierTypeRateGroup.rateZone}</span>
										</div>
									)}
								</div>
							</div>

							<BaasicDropdown
								className="dropdown--rate-calc spc--bottom--med"
								dropdownStore={carrierTypeRateGroup.dropdownStore}
							/>
							<RateList {...props} {...{ carrierTypeRateGroup }} />
						</div>
					</div>
				);
			})}
		</React.Fragment>
	);
});

const RateList = observer(props => {
	const { carrierTypeRateGroup, form } = props;
	return (
		<ul className="rate-calc__list">
			{_.map(carrierTypeRateGroup.dropdownStore.value.extra.rates, rate => {
				return (
					<li className="rate-calc__list__item__wrap" key={rate.listKey}>
						<div
							className="rate-calc__list__item k-custom-clickable"
							onClick={() => {
								rate.showDetail = !rate.showDetail;
							}}
						>
							<div className="flex--default rate-calc__list__dropdown flex--gap--1">
								<i
									className={
										rate.showDetail
											? 'rate-calc__icon fa fa-chevron-up flex--shrink--0'
											: 'rate-calc__icon fa fa-chevron-right flex--shrink--0'
									}
								/>
								<strong>{rate.service}</strong>
							</div>
							<div className="rate-calc__list__group">
								{form.$('showCheapestRate').value == true && (
									<span className="rate-calc__list__account" title={rate.cheapestCarrierAccount}>
										{rate.cheapestCarrierAccount}
									</span>
								)}
								<span className="rate-calc__list__price">${rate.value}</span>
							</div>
						</div>
						{rate.showDetail && <RateListDetail rate={rate} surcharges={rate.surcharges} {...props} />}
					</li>
				);
			})}
		</ul>
	);
});

const RateListDetail = observer(({ rate, surcharges, t }) => {
	return (
		<React.Fragment>
			<ul className="rate-calc__list__sub">
				{surcharges.map(surcharge => (
					<li key={surcharge.listKey}>
						<span>{surcharge.surchargeType}</span>
						<span>${surcharge.amount}</span>
					</li>
				))}
				<li>
					<span>{t('RATECALCULATOR.DELIVERYTIME')}</span>
					<span>{rate.deliveryTimestamp != null ? formatDate(rate.deliveryTimestamp, true, 'LLL') : '-'}</span>
				</li>
			</ul>
		</React.Fragment>
	);
});

const ImportParametersXlsModal = defaultTemplate(
	({
		modalParams: {
			data: { onFilesDrop, downloadTemplate, loading },
		},
	}) => {
		if (loading) return <Loader />;
		return (
			<React.Fragment>
				<div className="ReactModal__header">
					<div className="flex--secondary">
						<h3 className="spc--bottom--sml">{translate('RATECALCULATOR.MODAL.TITLE')}</h3>
					</div>
				</div>
				<div className="ReactModal__body">
					<div className="row">
						<div className="col col-sml-12 col-med-12">
							<BaasicDropzone
								acceptFiles={'.xls, .xlsx, .csv, text/plain'}
								onFilesDrop={onFilesDrop}
								placeholder={translate('RATECALCULATOR.DROPZONEPLACEHOLDER')}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col col-sml-3 col-med-2">
							<BaasicButton
								className="btn btn--med btn--primary spc--bottom--sml"
								type="button"
								onClick={downloadTemplate}
								label={'RATECALCULATOR.DOWNLOADTEMPLATE'}
							/>
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}
);

export default defaultTemplate(RateCalculatorTemplate);



// WEBPACK FOOTER //
// ./src/themes/default/modules/rate-calculator/pages/RateCalculatorTemplate.jsx